// Fingerprint-index candidate discovery — the joins dock reads the index,
// not the transcript.
//
// The index IS the string list: one trace-anchored co-carriage traversal
// (the trace's fingerprints self-joined to their co-carried rows over
// fp_hash) returns every fingerprint this trace shares with any other
// session, with its document frequency — over ALL of the trace's content,
// not a client-derived needle sample. The client never re-derives what the
// extraction already computed; deriving needles client-side and
// reconciling them against stored snippets was the source of every recall
// trap this file has shed (clip geometry, containment direction, window
// mismatch).
//
// Each shared string lands in one class:
//
//   banded   (2 <= df <= cap+1)  -> carrier rows, counted joins
//   diffuse  (df > cap+1)        -> too common to count, no expansion
//
// and the result is fold-ready: one needle entry per string (its text is
// the stored snippet, which occurs verbatim in the transcript, so the
// caller's locator can stamp turn/cell pins), one aligned result carrying
// the carrier sessions. The focal trace's own role mask supplies the
// direction evidence.
//
// Two honest degradations relative to content probes, both structural to
// the index: role granularity is the stored side mask (assistant vs
// user/tool — direction evidence survives, "in a tool result" does not),
// and `at` is the fingerprint's first observation in the window, not each
// occurrence.
//
// The backend this reads — the fingerprint table, its ingest MV, and the
// planner's entity and co-carried edge — is merged and live. The schema
// probe and the caller's probe fallback exist for older deployed gateways
// and for history the backfill has not reached, not as a merge-order
// hedge.

import { runQuery, rowsToObjects, describeSchema } from "./analyticsClient.js";
import { PROBE_SESSION_CAP } from "./traceElsewhereChannels.js";

const FP_ENTITY = "ENTITY_TYPE_CONVERSATION_FINGERPRINT";
const FP = (alias, name) => ({
  stepAlias: alias,
  field: `DIMENSION_FIELD_CONVERSATION_FINGERPRINT_${name}`,
});

// The index's role mask: bit 1 = assistant side, bit 2 = user/tool side.
// One mask expands to up to two pseudo-rows so the fold's direction
// arithmetic (sent = user|tool side, received = assistant side) reads the
// same evidence it reads from content-probe rows.
export function rolesForMask(mask) {
  const roles = [];
  const m = Number(mask) || 0;
  if (m & 1) roles.push("assistant");
  if (m & 2) roles.push("user");
  return roles;
}

function absoluteRange(startTime, endTime) {
  return { absolute: { start: startTime, end: endTime } };
}

let _fpEntityAvailable = null;
// Whether the deployed gateway knows the fingerprint entity at all — older
// backends must fall back to content probes, not error. The schema document
// is memoized upstream; string containment keeps this independent of its
// exact shape. The co-carried edge shipped with the entity's stack, so one
// probe covers both.
export async function fingerprintEntityAvailable() {
  if (_fpEntityAvailable !== null) return _fpEntityAvailable;
  try {
    const schema = await describeSchema();
    _fpEntityAvailable = JSON.stringify(schema).includes("CONVERSATION_FINGERPRINT");
  } catch {
    // Leave undetermined: a transient schema failure must not pin the
    // fallback for the whole session.
    return false;
  }
  return _fpEntityAvailable;
}

export function _resetFpEntityProbeForTests() {
  _fpEntityAvailable = null;
}

// The reads are bounded by these, not by trace size: the traversal returns
// at most TRAVERSAL_HASH_BUDGET shared fingerprints (rarest first, so a cut
// sheds the least-distinctive evidence), the dock surfaces the rarest
// SHARED_STRING_CAP of them, and carriers expand only that surfaced subset,
// chunked so 16 hashes x band-max carriers x up to 2 side-rows x part
// splits stays under the entity's 1000-row record cap.
// Sized to hold a restored-capture trace's WHOLE band (tool-input content
// puts thousands of df=2 strings ahead of the broader df 3..13 tier, and a
// clipped page starves the session inventory); the planner's aggregate
// cap is 10k.
export const TRAVERSAL_HASH_BUDGET = 4000;
export const SHARED_STRING_CAP = 192;
export const SESSION_INVENTORY_CAP = 200;
const COVERAGE_SESSION_CAP = 16;
// The planner rejects IN lists above 1024 values.
const INVENTORY_HASH_CHUNK = 1024;
const DIFFUSE_SAMPLE_CAP = 24;
const LANE_STRING_CAP = 48;
// Hand-off grams: a contiguous run per text (see handoffGrams), and a cap on
// the whole ask that keeps it one IN list under the validator's 1024.
export const HANDOFF_GRAMS_PER_TEXT = 96;
export const HANDOFF_STRING_CAP = 384;
const CARRIER_HASH_CHUNK = 16;
const CARRIER_FETCH_CONCURRENCY = 3;

// Every fingerprint this trace shares with any other session, rarest
// first: trace-anchored fingerprints self-joined to their co-carried rows
// over fp_hash, grouped per hash with the run's snippet, document
// frequency from the joined side. HAVING df >= 2 drops the trace-unique
// majority server-side; the over-band rows stay, because "too common" is
// an answer. df counts the focal trace itself.
//
// Cost shape: the joined side's postings are read before the band
// applies, so one open costs the sum of the trace's shared strings'
// document frequencies WITHIN THE WINDOW — the server-side HAVING and
// TRAVERSAL_HASH_BUDGET bound what returns, not what is scanned. Three
// things bound the scan today:
//   1. The window is hard-capped at the entity's 30-day span (the caller
//      pads to at most CONVERSATION_SPAN_MAX_MS), never table retention.
//   2. The planner restricts the joined side to the anchored trace's own
//      hashes, so the join holds only postings that can match: at the full
//      cap on a tenant with 58M postings, a 232K-hash trace measured 0.4 GiB
//      and under a second.
//   3. The analytics executor's runtime row/byte/time limits cap the
//      pathological case as a clean per-query error, which the caller's
//      whole-path fallback already handles.
// What grows with fleet size is the in-window df of boilerplate strings.
// The durable bound for that is the per-hash df rollup, and the banding
// above now prefers it whenever the entity is deployed (see
// bandTraceHashesViaRollup). This traversal remains the answer for three
// populations: older backends without the rollup, hash lists too big to
// hold client-side (the server-side rarest-first is the right shape for
// those), and rollup coverage gaps — reading an unbackfilled rollup
// undercounts df and silently drops real joins, which is exactly what the
// rollup path's coverage self-check refuses to do.
async function fetchCoCarriage({ traceId, startTime, endTime, signal }) {
  const spec = {
    root: {
      alias: "f1",
      entity: FP_ENTITY,
      children: [
        {
          edge: "EDGE_TYPE_CONVERSATION_FINGERPRINT_CO_CARRIED",
          mode: "JOIN_MODE_INNER",
          target: { alias: "f2", entity: FP_ENTITY },
        },
      ],
    },
    timeRange: absoluteRange(startTime, endTime),
    filter: {
      pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) },
    },
    output: {
      dimensions: [
        { field: FP("f1", "FP_HASH"), outputAlias: "fp_hash" },
        { field: FP("f1", "SNIPPET"), outputAlias: "snippet" },
      ],
      metrics: [
        {
          outputAlias: "df",
          computed: { field: FP("f2", "TRACE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" },
        },
      ],
    },
    having: [{ metricAlias: "df", op: "OPERATOR_GTE", value: 2 }],
    orderBy: [{ outputAlias: "df", direction: "DIRECTION_ASC" }],
    limit: TRAVERSAL_HASH_BUDGET,
  };
  return rowsToObjects(await runQuery(spec, { signal }));
}

// Whether the index holds this trace at all — the line between "the index
// answered: nothing is shared" (an honest empty) and "the index cannot
// speak for this trace" (the caller should fall back to content probes).
async function traceIsIndexed({ traceId, startTime, endTime, signal }) {
  const spec = {
    root: { alias: "f1", entity: FP_ENTITY },
    timeRange: absoluteRange(startTime, endTime),
    filter: {
      pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) },
    },
    output: {
      dimensions: [{ field: FP("f1", "FP_HASH"), outputAlias: "fp_hash" }],
    },
    limit: 1,
  };
  return rowsToObjects(await runQuery(spec, { signal })).length > 0;
}

// ── Banding via the df rollup ────────────────────────────────────────────────
//
// Two bounded reads replace the co-carriage traversal's posting scan when the
// rollup entity is deployed: the trace's OWN hash list (keyset-paged; its
// cost is the trace's own content, never the fleet's), then merged carrier
// counts for that list from per-(hash, day) states. The hash list stays a
// client-side product on purpose — it is the needle list the dock renders,
// the key the carrier/inventory/coverage reads reuse, and the seam where a
// coverage gap is VISIBLE (a hash the index returned but the rollup does not
// know means the backfill has not reached this data, and the traversal below
// remains the honest answer). Deliberately not folded into one joined query:
// fold intermediates, expose products — the traversal's posting lists were a
// correct fold; this list is the product.
const DF_ROLLUP_ENTITY = "ENTITY_TYPE_CONVERSATION_FINGERPRINT_DF_DAILY";
const DFR = (alias, name) => ({
  stepAlias: alias,
  field: `DIMENSION_FIELD_CONVERSATION_FINGERPRINT_DF_${name}`,
});
// One response returns at most the entity's record cap, so a page is ~a
// thousand hashes. The budget covers the measured p99 trace (~15k distinct
// hashes) with headroom; the rare monster beyond it keeps the traversal,
// whose server-side rarest-first is the right shape for a hash list too big
// to hold.
const HASH_PAGE_ROWS = 1000;
const HASH_PAGE_BUDGET = 20;
// The validator caps IN lists at 1024 values.
const BAND_HASH_CHUNK = 1024;
const BAND_FETCH_CONCURRENCY = 4;

let _dfRollupAvailable = null;
// Whether the deployed backend knows the rollup entity — older gateways run
// the traversal, same posture as the index probe above.
export async function dfRollupAvailable() {
  if (_dfRollupAvailable !== null) return _dfRollupAvailable;
  try {
    const schema = await describeSchema();
    _dfRollupAvailable = JSON.stringify(schema).includes("CONVERSATION_FINGERPRINT_DF_DAILY");
  } catch {
    return false;
  }
  return _dfRollupAvailable;
}

export function _resetDfRollupProbeForTests() {
  _dfRollupAvailable = null;
}

// The edge from a trace's fingerprints to the df rollup on fp_hash. The
// planner collapses it: the trace's hashes become a membership set inside
// the rollup read, so the banding is one request and the hashes never
// leave the server. Older gateways lack the edge; the schema document
// says so once, and the rollup path below is the fallback.
const DF_BY_HASH_EDGE = "EDGE_TYPE_CONVERSATION_FINGERPRINT_DF_BY_HASH";
let _dfByHashEdgeAvailable = null;
export async function dfByHashEdgeAvailable() {
  if (_dfByHashEdgeAvailable !== null) return _dfByHashEdgeAvailable;
  try {
    const schema = await describeSchema();
    _dfByHashEdgeAvailable = JSON.stringify(schema).includes(DF_BY_HASH_EDGE);
  } catch {
    return false;
  }
  return _dfByHashEdgeAvailable;
}

export function _resetDfByHashEdgeProbeForTests() {
  _dfByHashEdgeAvailable = null;
}

// One read per hash-range page: the trace's fingerprints collapse into a
// membership set on the rollup, the carrier count merged per hash, shared
// hashes only (df >= 2), rarest first, within the traversal budget. The collapse has two rules
// the gateway enforces: the trace anchor sits in the root's step filter,
// not the top-level filter, and the root contributes no output, so the
// rollup's own fp_hash is the only dimension and snippets come from
// `fetchTraceSnippets` for the rows that surface. A trace with no shared
// hash returns no rows, which is not the same as an un-indexed trace: the
// caller settles that with the indexed check.
function dfHashPageBounds(fingerprints) {
  if (fingerprints <= 2000) return [];
  if (fingerprints <= 5500) return ["1410506802960811800"];
  return ["634098555992646400", "1410506802960811800", "2691825117752661000"];
}

function dfHashPages(bounds) {
  const edges = [null, ...bounds, null];
  return edges.slice(0, -1).map((low, i) => [
    ...(low ? [{ pred: { field: DFR("df", "FP_HASH"), op: "OPERATOR_GTE", value: low } }] : []),
    ...(edges[i + 1] ? [{ pred: { field: DFR("df", "FP_HASH"), op: "OPERATOR_LT", value: edges[i + 1] } }] : []),
  ]);
}

async function countTraceFingerprints({ traceId, startTime, endTime, signal }) {
  const spec = {
    root: { alias: "f1", entity: FP_ENTITY },
    timeRange: absoluteRange(startTime, endTime),
    filter: {
      pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) },
    },
    output: {
      metrics: [{ outputAlias: "fingerprints", computed: { field: FP("f1", "FP_HASH"), op: "AGGREGATE_OP_COUNT" } }],
    },
    limit: 1,
  };
  const rows = rowsToObjects(await runQuery(spec, { signal }));
  return Number(rows[0]?.fingerprints) || 0;
}

async function bandTraceHashesViaEdge({ traceId, startTime, endTime, signal }) {
  const fingerprints = await countTraceFingerprints({ traceId, startTime, endTime, signal });
  if (fingerprints === 0) return [];
  const pages = await Promise.all(dfHashPages(dfHashPageBounds(fingerprints))
    .map((range) => bandTraceHashPage({ traceId, startTime, endTime, signal, range })));
  return pages.flat().sort((a, b) => a.df - b.df).slice(0, TRAVERSAL_HASH_BUDGET);
}

async function bandTraceHashPage({ traceId, startTime, endTime, signal, range }) {
  const spec = {
    root: {
      alias: "f1",
      entity: FP_ENTITY,
      stepFilter: [
        { pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) } },
      ],
      children: [
        {
          edge: DF_BY_HASH_EDGE,
          mode: "JOIN_MODE_INNER",
          target: { alias: "df", entity: DF_ROLLUP_ENTITY, stepFilter: range },
        },
      ],
    },
    timeRange: absoluteRange(startTime, endTime),
    output: {
      dimensions: [{ field: DFR("df", "FP_HASH"), outputAlias: "fp_hash" }],
      metrics: [
        {
          outputAlias: "df",
          standard: { stepAlias: "df", metric: "METRIC_TYPE_CONVERSATION_FINGERPRINT_DF_CARRIER_TRACES" },
        },
      ],
    },
    having: [{ metricAlias: "df", op: "OPERATOR_GTE", value: 2 }],
    orderBy: [{ outputAlias: "df", direction: "DIRECTION_ASC" }],
    limit: TRAVERSAL_HASH_BUDGET,
  };
  const rows = rowsToObjects(await runQuery(spec, { signal }));
  return rows.map((r) => ({ fp_hash: String(r.fp_hash), snippet: null, df: Number(r.df) || 0 }));
}

// The trace's own stored text for the hashes that surface: one trace-anchored
// read on the by-trace projection, at most a few hundred hashes, so the
// membership band above can stay output-free. One hash is one run, so any
// row's snippet is the run's own words.
async function fetchTraceSnippets({ traceId, hashes, startTime, endTime, signal }) {
  const bySnippetHash = new Map();
  for (let i = 0; i < hashes.length; i += BAND_HASH_CHUNK) {
    const chunk = hashes.slice(i, i + BAND_HASH_CHUNK);
    const spec = {
      root: { alias: "f1", entity: FP_ENTITY },
      timeRange: absoluteRange(startTime, endTime),
      filter: {
        andExpr: {
          children: [
            { pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) } },
            { pred: { field: FP("f1", "FP_HASH"), op: "OPERATOR_IN", value: chunk } },
          ],
        },
      },
      output: {
        dimensions: [
          { field: FP("f1", "FP_HASH"), outputAlias: "fp_hash" },
          { field: FP("f1", "SNIPPET"), outputAlias: "snippet" },
        ],
      },
      limit: chunk.length,
    };
    for (const r of rowsToObjects(await runQuery(spec, { signal }))) {
      const h = String(r.fp_hash);
      if (!bySnippetHash.has(h) && r.snippet) bySnippetHash.set(h, r.snippet);
    }
  }
  return bySnippetHash;
}

// The trace's distinct-hash count: sizes the pagination, and doubles as the
// indexed-at-all check (zero means the fallback probes are the only route).
async function fetchTraceHashCount({ traceId, startTime, endTime, signal }) {
  const spec = {
    root: { alias: "f1", entity: FP_ENTITY },
    timeRange: absoluteRange(startTime, endTime),
    filter: {
      pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) },
    },
    output: {
      metrics: [
        {
          outputAlias: "hashes",
          computed: { field: FP("f1", "FP_HASH"), op: "AGGREGATE_OP_COUNT_DISTINCT" },
        },
      ],
    },
    limit: 1,
  };
  const rows = rowsToObjects(await runQuery(spec, { signal }));
  return Number(rows[0]?.hashes) || 0;
}

// The trace's own (hash, snippet) list, keyset-paged on fp_hash. Any single
// row completes a hash (one hash is one run, one snippet), so the cursor
// advances with a strict GT and a page-boundary hash split across responses
// loses nothing.
async function fetchTraceHashList({ traceId, startTime, endTime, signal }) {
  const hashes = new Map();
  let cursor = null;
  for (let page = 0; page < HASH_PAGE_BUDGET; page++) {
    const children = [
      { pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) } },
    ];
    if (cursor != null) {
      children.push({ pred: { field: FP("f1", "FP_HASH"), op: "OPERATOR_GT", value: cursor } });
    }
    const spec = {
      root: { alias: "f1", entity: FP_ENTITY },
      timeRange: absoluteRange(startTime, endTime),
      filter: children.length > 1 ? { andExpr: { children } } : children[0],
      output: {
        dimensions: [
          { field: FP("f1", "FP_HASH"), outputAlias: "fp_hash" },
          { field: FP("f1", "SNIPPET"), outputAlias: "snippet" },
        ],
      },
      orderBy: [{ outputAlias: "fp_hash", direction: "DIRECTION_ASC" }],
      limit: HASH_PAGE_ROWS,
    };
    const rows = rowsToObjects(await runQuery(spec, { signal }));
    for (const r of rows) {
      const h = String(r.fp_hash);
      if (!hashes.has(h)) hashes.set(h, r.snippet);
    }
    if (rows.length < HASH_PAGE_ROWS) return { hashes, complete: true };
    cursor = String(rows[rows.length - 1].fp_hash);
  }
  return { hashes, complete: false };
}

// Merged carrier counts for a hash list: chunked IN binds against the
// rollup, one row per hash, df exact through 13 and pinned at 14 beyond.
async function fetchDfBands({ hashes, startTime, endTime, signal }) {
  const chunks = [];
  for (let i = 0; i < hashes.length; i += BAND_HASH_CHUNK) {
    chunks.push(hashes.slice(i, i + BAND_HASH_CHUNK));
  }
  const df = new Map();
  let next = 0;
  const worker = async () => {
    while (next < chunks.length) {
      const chunk = chunks[next++];
      if (signal?.aborted) return;
      const spec = {
        root: { alias: "df", entity: DF_ROLLUP_ENTITY },
        timeRange: absoluteRange(startTime, endTime),
        filter: {
          pred: { field: DFR("df", "FP_HASH"), op: "OPERATOR_IN", value: chunk },
        },
        output: {
          dimensions: [{ field: DFR("df", "FP_HASH"), outputAlias: "fp_hash" }],
          metrics: [
            {
              outputAlias: "df",
              standard: {
                stepAlias: "df",
                metric: "METRIC_TYPE_CONVERSATION_FINGERPRINT_DF_CARRIER_TRACES",
              },
            },
          ],
        },
        limit: BAND_HASH_CHUNK,
      };
      const rows = rowsToObjects(await runQuery(spec, { signal }));
      for (const r of rows) df.set(String(r.fp_hash), Number(r.df) || 0);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(BAND_FETCH_CONCURRENCY, chunks.length) }, worker),
  );
  return df;
}

// The rollup path end to end. Returns { indexed: false } for an un-indexed
// trace, { indexed: true, shared } with the traversal's exact row shape
// ({ fp_hash, snippet, df }, rarest first) on success, and
// { indexed: true, shared: null } when the traversal should answer instead:
// a hash list too big to hold, a truncated page walk, or a rollup coverage
// gap. A diffuse row's df arrives as the band sentinel 14 rather than the
// true fleet count — downstream reads the diffuse FLAG, never that number.
async function bandTraceHashesViaRollup({ traceId, startTime, endTime, signal }) {
  const count = await fetchTraceHashCount({ traceId, startTime, endTime, signal });
  if (count === 0) return { indexed: false };
  if (count > HASH_PAGE_ROWS * HASH_PAGE_BUDGET) return { indexed: true, shared: null };
  const { hashes, complete } = await fetchTraceHashList({ traceId, startTime, endTime, signal });
  if (!complete || hashes.size === 0) return { indexed: true, shared: null };
  const bands = await fetchDfBands({
    hashes: [...hashes.keys()], startTime, endTime, signal,
  });
  // The coverage self-check the two-read shape exists for: every hash the
  // index returned in this window must have a rollup row in the same
  // window. A miss means the per-tenant backfill has not reached this data
  // and every count would read rarer than it is — silently dropping real
  // joins — so the traversal stays the answer until it has.
  if (bands.size < hashes.size) return { indexed: true, shared: null };
  const shared = [];
  for (const [fpHash, snippet] of hashes) {
    const df = bands.get(fpHash) || 0;
    if (df >= 2) shared.push({ fp_hash: fpHash, snippet, df });
  }
  shared.sort((a, b) => a.df - b.df || (a.fp_hash < b.fp_hash ? -1 : 1));
  return { indexed: true, shared: shared.slice(0, TRAVERSAL_HASH_BUDGET) };
}

// Carrier rows for the surfaced hashes, snippet included (one hash means
// one run text, so any row's snippet is the run's own words).
// first_event_time and role_mask ride as dimensions — the grain may hold
// several part-rows per pair; the assembly collapses.
async function fetchFingerprintCarriers({ hashes, startTime, endTime, signal }) {
  const chunks = [];
  for (let i = 0; i < hashes.length; i += CARRIER_HASH_CHUNK) {
    chunks.push(hashes.slice(i, i + CARRIER_HASH_CHUNK));
  }
  const out = [];
  let next = 0;
  const worker = async () => {
    while (next < chunks.length) {
      const chunk = chunks[next++];
      if (signal?.aborted) return;
      const spec = {
        root: { alias: "f1", entity: FP_ENTITY },
        timeRange: absoluteRange(startTime, endTime),
        filter: { pred: { field: FP("f1", "FP_HASH"), op: "OPERATOR_IN", value: chunk } },
        output: {
          dimensions: [
            { field: FP("f1", "FP_HASH"), outputAlias: "fp_hash" },
            { field: FP("f1", "TRACE_ID"), outputAlias: "session_id" },
            { field: FP("f1", "ROLE_MASK"), outputAlias: "role_mask" },
            { field: FP("f1", "FIRST_EVENT_TIME"), outputAlias: "at" },
            { field: FP("f1", "SNIPPET"), outputAlias: "snippet" },
          ],
        },
        limit: 1000,
      };
      out.push(...rowsToObjects(await runQuery(spec, { signal })));
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CARRIER_FETCH_CONCURRENCY, chunks.length) }, worker),
  );
  return out;
}

// Who shares content with this trace, exactly: the same self-join grouped
// by the COUNTERPART, restricted to the banded strings, so the session
// list and count are definitional rather than an artifact of whichever
// strings fit a surfacing cap. min/max of the stored side mask together
// recover which sides the counterpart touched (min 1 = an assistant-only
// row exists, max >= 2 = a user/tool row exists).
async function fetchSessionInventory({ traceId, bandedHashes, startTime, endTime, signal }) {
  const spec = {
    root: {
      alias: "f1",
      entity: FP_ENTITY,
      children: [
        {
          edge: "EDGE_TYPE_CONVERSATION_FINGERPRINT_CO_CARRIED",
          mode: "JOIN_MODE_INNER",
          target: { alias: "f2", entity: FP_ENTITY },
        },
      ],
    },
    timeRange: absoluteRange(startTime, endTime),
    filter: {
      andExpr: {
        children: [
          { pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) } },
          { pred: { field: FP("f1", "FP_HASH"), op: "OPERATOR_IN", value: bandedHashes } },
        ],
      },
    },
    output: {
      dimensions: [{ field: FP("f2", "TRACE_ID"), outputAlias: "session_id" }],
      metrics: [
        { outputAlias: "shared", computed: { field: FP("f2", "FP_HASH"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
        { outputAlias: "first_seen", computed: { field: FP("f2", "FIRST_EVENT_TIME"), op: "AGGREGATE_OP_MIN" } },
        { outputAlias: "role_min", computed: { field: FP("f2", "ROLE_MASK"), op: "AGGREGATE_OP_MIN" } },
        { outputAlias: "role_max", computed: { field: FP("f2", "ROLE_MASK"), op: "AGGREGATE_OP_MAX" } },
      ],
    },
    orderBy: [{ outputAlias: "shared", direction: "DIRECTION_DESC" }],
    limit: SESSION_INVENTORY_CAP,
  };
  return rowsToObjects(await runQuery(spec, { signal }));
}

// Example strings for top counterparts the surfaced set happened to miss:
// their own rows over the focal trace's banded strings. Arbitrary rows per
// session (the record cap bounds the read), enough for pins and excerpts —
// counts never depend on this read.
async function fetchSessionCoverage({ traceId, sessionIds, startTime, endTime, signal }) {
  const spec = {
    root: {
      alias: "f1",
      entity: FP_ENTITY,
      children: [
        {
          edge: "EDGE_TYPE_CONVERSATION_FINGERPRINT_CO_CARRIED",
          mode: "JOIN_MODE_INNER",
          target: {
            alias: "f2",
            entity: FP_ENTITY,
            stepFilter: [
              { pred: { field: FP("f2", "TRACE_ID"), op: "OPERATOR_IN", value: sessionIds } },
            ],
          },
        },
      ],
    },
    timeRange: absoluteRange(startTime, endTime),
    filter: {
      pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) },
    },
    output: {
      dimensions: [
        { field: FP("f2", "TRACE_ID"), outputAlias: "session_id" },
        { field: FP("f1", "FP_HASH"), outputAlias: "fp_hash" },
        { field: FP("f1", "SNIPPET"), outputAlias: "snippet" },
        { field: FP("f2", "ROLE_MASK"), outputAlias: "role_mask" },
        { field: FP("f2", "FIRST_EVENT_TIME"), outputAlias: "at" },
      ],
    },
    limit: 1000,
  };
  return rowsToObjects(await runQuery(spec, { signal }));
}

// The run-class projection of a lane path: the segments its text would
// have been fingerprinted as (lowercased, split at characters outside the
// run alphabet), plus the extensionless basename. The lane's own name is
// how a file-mediated hand-off is matched, so these strings are fetched
// deterministically rather than left to a surfacing lottery.
export function lanePathRuns(path) {
  const lower = String(path || "").toLowerCase();
  const segments = lower.split(/[^a-z0-9+/=_-]+/).filter((seg) => seg.length >= 24);
  const base = lower.split(/[\/]/).pop() || "";
  const stem = base.replace(/\.[a-z0-9]{1,8}$/, "");
  if (stem.length >= 24) segments.push(stem);
  return [...new Set(segments)];
}

/**
 * A hand-off's own words as the index stores them: the lowercased text with
 * its escaped line breaks, tabs, quotes and slashes restored, split on
 * whitespace, eight words at a time joined by single spaces and clipped to
 * 90 characters - the ingest view's gram snippet, character for character.
 * The view keeps a winnowed subset, the minimum of each window of sixteen,
 * so this asks for a CONTIGUOUS run of grams: every window it covers holds
 * one the index kept, where an even sample would mostly miss them.
 */
export function handoffGrams(text, { max = HANDOFF_GRAMS_PER_TEXT } = {}) {
  let norm = String(text || "").toLowerCase();
  for (let pass = 0; pass < 2; pass += 1) {
    norm = norm.replace(/\\r\\n|\\n|\\r|\\t/g, " ").replace(/\\"/g, '"').replace(/\\\//g, "/");
  }
  const words = norm.split(/\s+/).filter(Boolean).slice(0, 2048);
  const out = new Set();
  for (let i = 0; i + 8 <= words.length && out.size < max; i += 1) {
    out.add([...words.slice(i, i + 8).join(" ")].slice(0, 90).join(""));
  }
  return [...out];
}

/**
 * The provider-encrypted task tokens in a hand-off's text, as the index
 * stores a run: lowercased and clipped to its first 90 characters. Codex
 * hands a child its task as the same token (Fernet, `gAAAAA...`) the
 * spawning call carries, so the token is the run both sides share.
 */
export function sealedTaskRuns(text) {
  const runs = String(text || "").toLowerCase().match(/[a-z0-9+/=_-]{24,}/g) || [];
  return [...new Set(runs.filter((run) => run.startsWith("gaaaaa")).map((run) => run.slice(0, 90)))];
}

/**
 * Who else carries a sealed task's token: the content join asked for one
 * needle. The focal trace's own row supplies the hash (the index keys a run
 * by the hash of all of it), and that hash's other carriers are the
 * conversations the token reached - for a Codex spawn, its child.
 * -> [{ sessionId, ingested, at }], `at` the earliest carriage.
 */
export async function sealedTaskCarriers({ traceId, token, startTime, endTime, signal = null } = {}) {
  const strings = sealedTaskRuns(token);
  if (!traceId || !strings.length) return [];
  const { carriers } = await fetchLaneIdentityCarriers({ traceId, strings, startTime, endTime, signal });
  const out = new Map();
  for (const c of carriers) {
    const sessionId = String(c.session_id || "");
    if (!sessionId || sessionId === String(traceId)) continue;
    const ingested = Boolean((Number(c.role_mask) || 0) & 2);
    const at = c.at ?? null;
    const prev = out.get(sessionId);
    if (!prev) out.set(sessionId, { sessionId, ingested, at });
    else {
      prev.ingested = prev.ingested || ingested;
      if (at != null && (prev.at == null || String(at) < String(prev.at))) prev.at = at;
    }
  }
  return [...out.values()];
}

// Deterministic carriers for the lane-identity strings, in two anchored
// hops: which of these strings does the FOCAL trace carry (trace-anchored
// snippet match), then who else carries those hashes (hash-anchored
// carriers). Focal carriage is the precondition for a join anyway, and it
// supplies the equality anchor the entity's access pattern demands.
async function fetchLaneIdentityCarriers({ traceId, strings, startTime, endTime, signal }) {
  if (!strings.length) return { hashes: [], carriers: [] };
  const focal = rowsToObjects(await runQuery({
    root: { alias: "f1", entity: FP_ENTITY },
    timeRange: absoluteRange(startTime, endTime),
    filter: {
      andExpr: {
        children: [
          { pred: { field: FP("f1", "TRACE_ID"), op: "OPERATOR_EQ", value: String(traceId) } },
          { pred: { field: FP("f1", "SNIPPET"), op: "OPERATOR_IN", value: strings } },
        ],
      },
    },
    output: {
      dimensions: [
        { field: FP("f1", "FP_HASH"), outputAlias: "fp_hash" },
        { field: FP("f1", "SNIPPET"), outputAlias: "snippet" },
      ],
    },
    limit: 500,
  }, { signal }));
  const hashes = [...new Set(focal.map((r) => String(r.fp_hash)))];
  if (!hashes.length) return { hashes: [], carriers: [] };
  const carriers = await fetchFingerprintCarriers({ hashes, startTime, endTime, signal });
  return { hashes, carriers };
}

// The dock's discovery pass. Returns `{ indexed, needles, results,
// sessions }`: `sessions` is the definitional inventory of counterparts
// (session id, how many banded strings it shares, first carriage, side
// coverage) — the dock's WHO, immune to any string cap. `needles` is the
// index's shared-string list (surfaced rarest strings plus example strings
// covering top counterparts the surfaced set missed; `turns`/`cells` are
// empty for the caller's locator to stamp), `results` aligns with it,
// fold-ready. A too-common string reports `{ ok: true, diffuse: true }` —
// counted by the shared partition without carrier expansion.
// `indexed: false` means the index holds nothing for this trace and the
// caller should probe instead; `indexed: true` with empty lists is the
// honest "nothing is shared".
export async function runFpNeedleJoin({ sessionId = null, startTime = null, endTime = null, laneStrings = [], handoffStrings = [], signal = null } = {}) {
  // Banding prefers one read over the fingerprint-to-rollup edge; the
  // rollup's two bounded reads remain for gateways without the edge, and
  // the traversal for older backends, hash lists too big to hold, and
  // rollup coverage gaps. Every route produces the same row shape, so
  // everything below the band is one code path.
  let shared = null;
  let indexed = false;
  if (await dfByHashEdgeAvailable()) {
    // A read the gateway refuses falls through to the paths below, so a
    // mixed rollout degrades to the old cost, never to no joins.
    let viaEdge = null;
    try {
      viaEdge = await bandTraceHashesViaEdge({ traceId: sessionId, startTime, endTime, signal });
    } catch (err) {
      if (signal?.aborted) throw err;
    }
    if (viaEdge) {
      indexed = viaEdge.length > 0 || await traceIsIndexed({ traceId: sessionId, startTime, endTime, signal });
      if (!indexed) return { indexed: false, needles: [], results: [], sessions: [], diffuseTotal: 0 };
      shared = viaEdge;
    }
  }
  const viaRollup = shared === null && (await dfRollupAvailable())
    ? await bandTraceHashesViaRollup({ traceId: sessionId, startTime, endTime, signal })
    : null;
  if (viaRollup && !viaRollup.indexed) {
    return { indexed: false, needles: [], results: [], sessions: [], diffuseTotal: 0 };
  }
  if (shared !== null) {
    // banded over the edge above
  } else if (viaRollup?.shared) {
    shared = viaRollup.shared;
    indexed = true;
  } else {
    // viaRollup non-null here means the hash-count preflight already proved
    // the trace indexed; don't spend the LIMIT-1 probe again.
    [shared, indexed] = await Promise.all([
      fetchCoCarriage({ traceId: sessionId, startTime, endTime, signal }),
      viaRollup ? Promise.resolve(true) : traceIsIndexed({ traceId: sessionId, startTime, endTime, signal }),
    ]);
  }
  if (!indexed) return { indexed: false, needles: [], results: [], sessions: [], diffuseTotal: 0 };

  const inBand = (r) => Number(r.df) <= PROBE_SESSION_CAP + 1;
  const bandedAll = shared.filter(inBand).map((r) => String(r.fp_hash));
  const surfaced = shared.filter(inBand).slice(0, SHARED_STRING_CAP);
  const bandedHashes = surfaced.map((r) => String(r.fp_hash));
  const overBand = shared.filter((r) => !inBand(r));
  // A representative sample keeps the tooltip's examples; the TRUE count
  // rides diffuseTotal so the strip never echoes a cap as a fact.
  const diffuseRows = overBand.slice(0, DIFFUSE_SAMPLE_CAP);
  // Rows banded over the edge arrive without text; only the rows that
  // surface need it, one small trace-anchored read.
  const textless = [...surfaced, ...diffuseRows].filter((r) => r.snippet == null);

  const [carriers, inventoryRaw, snippets] = await Promise.all([
    bandedHashes.length
      ? fetchFingerprintCarriers({ hashes: bandedHashes, startTime, endTime, signal })
      : Promise.resolve([]),
    bandedAll.length
      ? Promise.all(
        // The validator caps IN lists at 1024 values; the band can exceed
        // that on restored-capture traces. Chunk the inventory over
        // disjoint hash sets and merge per session — shared counts add,
        // first carriage takes the earliest, sides union.
        Array.from(
          { length: Math.ceil(bandedAll.length / INVENTORY_HASH_CHUNK) },
          (_, i) => bandedAll.slice(i * INVENTORY_HASH_CHUNK, (i + 1) * INVENTORY_HASH_CHUNK),
        ).map((chunk) =>
          fetchSessionInventory({ traceId: sessionId, bandedHashes: chunk, startTime, endTime, signal })),
      ).then((pages) => pages.flat())
      : Promise.resolve([]),
    textless.length
      ? fetchTraceSnippets({ traceId: sessionId, hashes: textless.map((r) => String(r.fp_hash)), startTime, endTime, signal })
      : Promise.resolve(new Map()),
  ]);
  for (const r of textless) r.snippet = snippets.get(String(r.fp_hash)) ?? null;
  const bySession = new Map();
  for (const r of inventoryRaw) {
    const id = String(r.session_id);
    if (id === String(sessionId)) continue;
    const prev = bySession.get(id);
    // Masks are 1 (assistant), 2 (user/tool), 3 (both). Across a session's
    // rows, min/max recover side presence: an assistant row exists iff min
    // is 1 or every row is 3; a user row iff max >= 2.
    const assistant = (Number(r.role_min) || 0) === 1 || (Number(r.role_max) || 0) === 3;
    const user = (Number(r.role_max) || 0) >= 2;
    const firstSeen = r.first_seen ?? null;
    if (!prev) {
      bySession.set(id, {
        session_id: id,
        shared: Number(r.shared) || 0,
        first_seen: firstSeen,
        sides: { assistant, user },
      });
    } else {
      prev.shared += Number(r.shared) || 0;
      if (firstSeen != null && (prev.first_seen == null || firstSeen < prev.first_seen)) {
        prev.first_seen = firstSeen;
      }
      prev.sides.assistant = prev.sides.assistant || assistant;
      prev.sides.user = prev.sides.user || user;
    }
  }
  const sessions = [...bySession.values()]
    .sort((a, b) => b.shared - a.shared)
    .slice(0, SESSION_INVENTORY_CAP);
  const carriersByHash = new Map();
  const focalMaskByHash = new Map();
  for (const row of carriers) {
    const h = String(row.fp_hash);
    if (!carriersByHash.has(h)) carriersByHash.set(h, []);
    carriersByHash.get(h).push(row);
    if (String(row.session_id) === String(sessionId)) {
      focalMaskByHash.set(h, (focalMaskByHash.get(h) || 0) | (Number(row.role_mask) || 0));
    }
  }

  const rowsForHash = (h, snippet) => {
    const rows = [];
    const seen = new Set();
    for (const c of carriersByHash.get(h) || []) {
      const sid = String(c.session_id || "");
      if (!sid) continue;
      for (const role of rolesForMask(c.role_mask)) {
        const dedup = `${sid}|${role}`;
        if (seen.has(dedup)) continue;
        seen.add(dedup);
        rows.push({
          session_id: sid,
          endpoint_id: null,
          role,
          part_kind: "",
          at: c.at ?? null,
          snippet,
          // The stored snippet IS the shared string, not surrounding
          // content — readers that want a real excerpt must hydrate.
          indexed: true,
        });
      }
    }
    return rows;
  };

  const needles = [];
  const results = [];
  const seenSnippets = new Set();
  const pushNeedle = (snippet, df, focalMask, rows) => {
    needles.push({
      needle: snippet,
      emit: Boolean(focalMask & 1),
      ingest: Boolean(focalMask & 2),
      turns: [],
      cells: [],
      indexed: true,
      // Ranking inputs: how widely the string is shared, and its class.
      // A stored run never contains a space; a word gram always does.
      df,
      gram: snippet.includes(" "),
    });
    results.push({ ok: true, rows });
  };
  for (const r of surfaced) {
    const h = String(r.fp_hash);
    const snippet = String(r.snippet || "").trim();
    if (!snippet || seenSnippets.has(snippet)) continue;
    seenSnippets.add(snippet);
    pushNeedle(snippet, Number(r.df) || 0, focalMaskByHash.get(h) || 0, rowsForHash(h, snippet));
  }
  for (const r of diffuseRows) {
    const snippet = String(r.snippet || "").trim();
    if (!snippet || seenSnippets.has(snippet)) continue;
    seenSnippets.add(snippet);
    needles.push({
      needle: snippet, emit: false, ingest: false, turns: [], cells: [],
      indexed: true, df: Number(r.df) || 0, gram: snippet.includes(" "),
    });
    results.push({ ok: true, diffuse: true, rows: [] });
  }

  // Top counterparts none of the surfaced strings reached still deserve
  // example strings for pins and excerpts — one bounded read over their
  // own rows on the focal trace's shared strings. Counts never depend on
  // this; it only keeps the strongest sessions from rendering evidence-
  // blank when the surfaced cap sampled past them.
  const covered = new Set();
  for (const rows of carriersByHash.values()) {
    for (const c of rows) covered.add(String(c.session_id));
  }
  const uncovered = sessions
    .filter((sess) => !covered.has(sess.session_id))
    .slice(0, COVERAGE_SESSION_CAP)
    .map((sess) => sess.session_id);
  if (uncovered.length) {
    const coverage = await fetchSessionCoverage({
      traceId: sessionId, sessionIds: uncovered, startTime, endTime, signal,
    });
    const bySnippet = new Map();
    for (const c of coverage) {
      const snippet = String(c.snippet || "").trim();
      if (!snippet || seenSnippets.has(snippet)) continue;
      if (!bySnippet.has(snippet)) bySnippet.set(snippet, []);
      const rows = bySnippet.get(snippet);
      const sid = String(c.session_id || "");
      for (const role of rolesForMask(c.role_mask)) {
        if (rows.some((row) => row.session_id === sid && row.role === role)) continue;
        rows.push({
          session_id: sid, endpoint_id: null, role, part_kind: "",
          at: c.at ?? null, snippet, indexed: true,
        });
      }
    }
    for (const [snippet, rows] of bySnippet) {
      if (!rows.length) continue;
      seenSnippets.add(snippet);
      // No df: the coverage read never learns document frequency, and a
      // fabricated value would let a backfilled string vouch its own
      // session out of the weak tier. Undefined stays out of every
      // rarity-weighted judgment; the entry exists for pins and excerpts.
      pushNeedle(snippet, undefined, 0, rows);
      needles[needles.length - 1].coverage = true;
    }
  }
  // Strings joined deterministically rather than by the surfaced cap: each
  // the focal trace carries gets its carriers, its real df, and a marker.
  const pushDeterministic = async (strings, marker) => {
    if (!strings.length) return;
    const { carriers: fixedCarriers } = await fetchLaneIdentityCarriers({
      traceId: sessionId, strings, startTime, endTime, signal,
    });
    const byLaneSnippet = new Map();
    for (const c of fixedCarriers) {
      const snippet = String(c.snippet || "").trim();
      if (!snippet || seenSnippets.has(snippet)) continue;
      if (!byLaneSnippet.has(snippet)) byLaneSnippet.set(snippet, []);
      byLaneSnippet.get(snippet).push(c);
    }
    for (const [snippet, rows] of byLaneSnippet) {
      const others = new Set(
        rows.map((c) => String(c.session_id)).filter((sid) => sid !== String(sessionId)),
      );
      if (!others.size) continue;
      seenSnippets.add(snippet);
      let focalMask = 0;
      const foldRows = [];
      const dedup = new Set();
      for (const c of rows) {
        const sid = String(c.session_id || "");
        if (sid === String(sessionId)) {
          focalMask |= Number(c.role_mask) || 0;
          continue;
        }
        for (const role of rolesForMask(c.role_mask)) {
          const key2 = `${sid}|${role}`;
          if (dedup.has(key2)) continue;
          dedup.add(key2);
          foldRows.push({
            session_id: sid, endpoint_id: null, role, part_kind: "",
            at: c.at ?? null, snippet, indexed: true,
          });
        }
      }
      const diffuseLane = others.size > PROBE_SESSION_CAP;
      needles.push({
        needle: snippet,
        emit: Boolean(focalMask & 1),
        ingest: Boolean(focalMask & 2),
        turns: [], cells: [],
        indexed: true,
        ...marker,
        df: others.size + 1, gram: snippet.includes(" "),
      });
      results.push(diffuseLane ? { ok: true, diffuse: true, rows: [] } : { ok: true, rows: foldRows });
    }
  };
  // Lane-identity strings: every file lane's own name the focal trace
  // carries. The cell pins' "matched by this file's name" badge keys on lane
  // containment of exactly these.
  await pushDeterministic(
    [...new Set((laneStrings || []).flatMap((s2) => lanePathRuns(s2)))]
      .filter((run) => !seenSnippets.has(run))
      .slice(0, LANE_STRING_CAP),
    { laneIdentity: true },
  );
  // Hand-off grams: the words an agents lane spawned with or was answered
  // with. A spawned thread is the one counterpart GUARANTEED to share them
  // verbatim, yet among thousands of equally rare strings the surfaced cap
  // left them to chance (found live: a child's report shared three grams
  // with its parent, and none of them surfaced).
  await pushDeterministic(
    [...new Set(handoffStrings || [])].filter((g) => g && !seenSnippets.has(g)).slice(0, HANDOFF_STRING_CAP),
    { handoff: true },
  );
  return { indexed: true, needles, results, sessions, diffuseTotal: overBand.length };
}
